import { Injectable, inject } from '@angular/core';
import { APP_CONFIG } from '../../core/config/app-config';
import type { MapLoaderPort } from '../../features/maps/domain/ports/map-loader.port';

declare global {
  interface Window {
    __ultrataxGoogleMapsReady?: () => void;
  }
}

/**
 * Adaptador Google Maps JavaScript API del `MapLoaderPort`.
 *
 * Inyecta el script una única vez (promesa cacheada) con callback + timeout.
 * Idempotente: si el SDK ya está listo o el script ya existe, no vuelve a
 * inyectarlo. Patrón heredado de la app del conductor.
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoader implements MapLoaderPort {
  private static readonly SCRIPT_ID = 'ultratax-google-maps';
  private static readonly TIMEOUT_MS = 20_000;

  private readonly config = inject(APP_CONFIG);
  private loadPromise?: Promise<void>;

  load(): Promise<void> {
    if (this.isReady()) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    const apiKey = this.config.googleMaps.apiKey;
    if (!apiKey || apiKey.includes('TU_API_KEY')) {
      return Promise.reject(
        new Error(
          'Configura environment.googleMaps.apiKey con una API Key válida de Google Maps Platform.',
        ),
      );
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        if (this.isReady()) {
          resolve();
          return;
        }
        this.loadPromise = undefined;
        reject(
          new Error(
            'Google Maps tardó demasiado en cargar. Revisa conexión, restricciones de la API Key o las APIs habilitadas.',
          ),
        );
      }, GoogleMapsLoader.TIMEOUT_MS);

      const done = (): void => {
        window.clearTimeout(timeout);
        resolve();
      };

      const fail = (): void => {
        window.clearTimeout(timeout);
        this.loadPromise = undefined;
        reject(new Error('No se pudo cargar Google Maps JavaScript API.'));
      };

      window.__ultrataxGoogleMapsReady = done;

      const existing = document.getElementById(
        GoogleMapsLoader.SCRIPT_ID,
      ) as HTMLScriptElement | null;
      if (existing) {
        if (this.isReady()) {
          done();
          return;
        }
        existing.addEventListener('load', done, { once: true });
        existing.addEventListener('error', fail, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = GoogleMapsLoader.SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.src = [
        'https://maps.googleapis.com/maps/api/js',
        `?key=${encodeURIComponent(apiKey)}`,
        '&libraries=geometry',
        '&v=weekly',
        '&language=es',
        '&region=MX',
        '&callback=__ultrataxGoogleMapsReady',
      ].join('');
      script.onerror = fail;
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  isReady(): boolean {
    const win = window as unknown as { google?: typeof google };
    return Boolean(win.google?.maps?.Map);
  }
}
